import { reportRequestFailure } from '../request-error-reporting.js';
import { fetchWithNetworkRetry } from '../network-request.js';
import { HttpRequestError, type RequestFailureContext } from '../../shared/request-error.js';
import { LitElement, type PropertyValues } from 'lit';
import { html } from 'lit/html.js';
import { getCurrentLocale } from '../i18n.js';
import { localePath, normalizeLocale } from '../../shared/locale.js';
import { CUSTOMER_ANALYTICS_SIGNUP_COPY } from '../../shared/customer-analytics-signup-copy.js';
import { CUSTOMER_ANALYTICS_COPY } from '../../shared/customer-analytics-copy.js';
import { CUSTOMER_ANALYTICS_POLICY_VERSION, type CustomerAnalyticsPreference } from '../../shared/customer-analytics.js';
import { clearCustomerAnalytics, refreshCustomerAnalytics } from '../customer-analytics-context.js';

export class CustomerAnalyticsPromptElement extends LitElement {
  static properties = { accountId: { attribute: false }, promptState: { state: true } };
  declare accountId: number | null;
  declare private promptState: 'hidden' | 'prompt' | 'saving' | 'refreshing' | 'failed' | 'hidden_failed';
  private get visible(): boolean { return this.promptState === 'prompt' || this.promptState === 'saving' || this.promptState === 'failed'; }
  private get busy(): boolean { return this.promptState === 'saving' || this.promptState === 'refreshing'; }
  private get error(): boolean { return this.promptState === 'failed' || this.promptState === 'hidden_failed'; }
  private generation = 0;
  private preferenceRead: { status: 'idle' | 'failed' } | { status: 'reading'; controller: AbortController } = { status: 'idle' };
  private retryPreferenceRead = () => {
    if (this.preferenceRead.status === 'failed' && this.accountId) void this.checkPreference(this.accountId, this.generation);
  };
  private resizeObserver: ResizeObserver | null = null;
  private cancelPreferenceRead = () => { if (this.preferenceRead.status === 'reading') this.preferenceRead.controller.abort(); };
  private resumePreferenceRead = (event?: PageTransitionEvent) => {
    if (event && !event.persisted) return;
    if (this.accountId) void this.checkPreference(this.accountId, this.generation);
  };

  constructor() {
    super();
    this.accountId = null;
    this.promptState = 'hidden';
  }

  createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pagehide', this.cancelPreferenceRead);
    window.addEventListener('pageshow', this.resumePreferenceRead);
    window.addEventListener('online', this.retryPreferenceRead);
    if (this.hasUpdated) this.resumePreferenceRead();
  }

  firstUpdated() {
    this.resizeObserver = new ResizeObserver(() => this.reserveBannerSpace());
    const banner = this.querySelector('.customer-consent-banner');
    if (banner) this.resizeObserver.observe(banner);
  }

  private reserveBannerSpace() {
    const height = this.visible ? this.querySelector('.customer-consent-banner')?.getBoundingClientRect().height ?? 0 : 0;
    document.documentElement?.style.setProperty('--layout-consent-banner-height', `${height ? height + 8 : 0}px`);
  }

  updated(changed: PropertyValues) {
    this.reserveBannerSpace();
    if (!changed.has('accountId')) return;
    this.cancelPreferenceRead();
    this.generation++;
    this.promptState = 'hidden';
    this.preferenceRead = { status: 'idle' };
    if (this.accountId) void this.checkPreference(this.accountId, this.generation);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cancelPreferenceRead();
    window.removeEventListener('pagehide', this.cancelPreferenceRead);
    window.removeEventListener('pageshow', this.resumePreferenceRead);
    window.removeEventListener('online', this.retryPreferenceRead);
    this.resizeObserver?.disconnect();
    document.documentElement?.style.removeProperty('--layout-consent-banner-height');
    this.generation++;
    this.promptState = 'hidden';
  }

  private async checkPreference(accountId: number, generation: number) {
    if (this.busy) return;
    this.cancelPreferenceRead();
    const controller = new AbortController();
    this.preferenceRead = { status: 'reading', controller };
    const startedAt = performance.now();
    let attempts = 0;
    const diagnostic: RequestFailureContext = { service: 'account-analytics', stage: 'fetch',
      page_path: window.location.pathname, response_received: false };
    try {
      if (navigator.onLine === false) { this.preferenceRead = { status: 'failed' }; return; }
      const response = await fetchWithNetworkRetry('/api/account/analytics', {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      }, { fetchImpl: (input, init) => {
        // Some browsers reject a cancelled fetch with TypeError, not AbortError.
        controller.signal.throwIfAborted();
        if (attempts++ > 0) diagnostic.request_trigger = 'retry';
        return fetch(input, init);
      } });
      diagnostic.response_received = true;
      diagnostic.http_status = response.status;
      diagnostic.response_type = response.type;
      if (!response.ok) throw new HttpRequestError('Preference was not read', response, 'account.analytics.preference.read');
      diagnostic.stage = 'decode';
      // SAFETY: The owned account endpoint supplies the preference. Identity and prompt state are checked below.
      const preference = await response.json() as CustomerAnalyticsPreference;
      diagnostic.stage = 'apply';
      if (controller.signal.aborted || generation !== this.generation) return;
      this.preferenceRead = { status: 'idle' };
      if (preference.account_id !== accountId || !preference.needs_prompt) return;
      this.promptState = 'prompt';
    } catch (cause) {
      if (!controller.signal.aborted && generation === this.generation) {
        this.preferenceRead = { status: 'failed' };
        reportRequestFailure(cause, 'account.analytics.preference.read', { ...diagnostic,
          elapsed_ms: Math.round(performance.now() - startedAt), online: navigator.onLine,
          visibility: document.visibilityState });
      }
    } finally {
      if (this.preferenceRead.status === 'reading' && this.preferenceRead.controller === controller) this.preferenceRead = { status: 'idle' };
    }
  }

  private async save(enabled: boolean) {
    if (this.busy) return;
    const generation = this.generation;
    this.cancelPreferenceRead();
    this.promptState = 'saving';
    try {
      const response = await fetch('/api/account/analytics', {
        method: 'PUT', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabled, account_id: this.accountId, only_if_unanswered: true, policy_version: CUSTOMER_ANALYTICS_POLICY_VERSION }),
      });
      if (!response.ok) throw new HttpRequestError('Preference was not saved', response, 'account.analytics.preference');
      if (generation !== this.generation) return;
      this.promptState = 'refreshing';
      clearCustomerAnalytics();
      await refreshCustomerAnalytics();
    } catch (cause) {
      reportRequestFailure(cause, 'account.analytics.preference.write');
      if (generation === this.generation) {
        this.promptState = enabled && this.promptState !== 'refreshing' ? 'failed' : 'hidden_failed';
      }
    }
    finally { if (generation === this.generation && this.promptState === 'refreshing') this.promptState = 'hidden'; }
  }

  render() {
    const locale = normalizeLocale(getCurrentLocale());
    const copy = CUSTOMER_ANALYTICS_SIGNUP_COPY[locale];
    return html`<aside class="customer-consent-banner mapui:fixed mapui:right-[var(--layout-edge)] chrome:bottom-[calc(var(--layout-occupied-bottom)_+_32px)] mapui:left-[var(--layout-edge)] mapui:z-[var(--layer-floating-panel)] mapui:flex mapui:flex-wrap mapui:items-center chrome:gap-[8px_16px] chrome:max-w-[700px] chrome:[margin-inline:auto] chrome:p-[12px_16px] chrome:[border:1px_solid_var(--border-color)] chrome:rounded-[10px] mapui:[background:var(--bg-toolbar)] mapui:[color:var(--text-primary)] chrome:[box-shadow:0_4px_18px_rgb(15_23_42_/_12%)] mapui:text-[13px] chrome:leading-[1.5] mapui:pointer-events-auto chrome:[&[hidden]]:hidden chrome:[&_[hidden]]:hidden chrome:[&_p]:[flex:1_1_300px] chrome:[&_p]:m-[0] chrome:w-max-768:bottom-[calc(var(--layout-edge)_+_env(safe-area-inset-bottom))]" aria-label=${copy.title} ?hidden=${!this.visible}>
      <p>${copy.banner} <a class="customer-consent-details mapui:[color:inherit] chrome:[text-decoration:underline]" href=${localePath('/privacy/#account-analytics', locale)} target="_blank" rel="noopener">${copy.learn}</a></p>
      <div class="customer-consent-actions mapui:flex mapui:gap-[8px] chrome:[&_button]:p-[8px_12px] chrome:[&_button]:[border:1px_solid_var(--border-color)] chrome:[&_button]:rounded-[6px] chrome:[&_button]:[background:var(--bg-toolbar)] chrome:[&_button]:[color:var(--text-primary)] chrome:[&_button]:font-inherit chrome:[&_button]:[cursor:pointer] chrome:[&_.customer-consent-allow]:[border-color:#15803d] chrome:[&_.customer-consent-allow]:[background:#15803d] chrome:[&_.customer-consent-allow]:[color:#fff]">
        <button type="button" class="customer-consent-allow" ?disabled=${this.busy} @click=${() => this.save(true)}>${copy.allow}</button>
        <button type="button" ?disabled=${this.busy} @click=${() => this.save(false)}>${copy.decline}</button>
      </div>
      <p role="status" ?hidden=${!this.error}>${CUSTOMER_ANALYTICS_COPY[locale].error}</p>
    </aside>`;
  }
}
customElements.define('customer-analytics-prompt', CustomerAnalyticsPromptElement);
